import { DEFAULT_COUNTRY_CODE, type CountryCode } from '../config';

type GoalAmountCurrencyConfig = {
  countryCode: CountryCode;
  currencySymbol: string;
  thousandsSeparator: string;
};

export const GOAL_AMOUNT_MIN = 100;
export const GOAL_AMOUNT_MAX = 1_000_000;

const GOAL_AMOUNT_CURRENCY_CONFIG: Record<
  CountryCode,
  Omit<GoalAmountCurrencyConfig, 'countryCode'>
> = {
  '+54': {
    currencySymbol: '$',
    thousandsSeparator: '.',
  },
  '+55': {
    currencySymbol: 'R$',
    thousandsSeparator: '.',
  },
};

function sanitizeGoalAmountDigits(value: string): string {
  return value.replace(/\D/g, '');
}

export function resolveGoalAmountCurrencyConfig(
  countryCode: CountryCode = DEFAULT_COUNTRY_CODE,
): GoalAmountCurrencyConfig {
  return {
    countryCode,
    ...GOAL_AMOUNT_CURRENCY_CONFIG[countryCode],
  };
}

export function resolveCurrencyPrefix(
  countryCode: CountryCode = DEFAULT_COUNTRY_CODE,
): string {
  return `${resolveCurrencySymbol(countryCode)} `;
}

export function resolveCurrencySymbol(
  countryCode: CountryCode = DEFAULT_COUNTRY_CODE,
): string {
  return resolveGoalAmountCurrencyConfig(countryCode).currencySymbol;
}

export function resolveThousandsSeparator(
  countryCode: CountryCode = DEFAULT_COUNTRY_CODE,
): string {
  return resolveGoalAmountCurrencyConfig(countryCode).thousandsSeparator;
}

export function parseAmount(value: string): number | null {
  const digits = sanitizeGoalAmountDigits(value);
  if (!digits) {
    return null;
  }

  const numericValue = Number.parseInt(digits, 10);
  return Number.isFinite(numericValue) ? numericValue : null;
}

export function formatCurrency(
  amount: number,
  countryCode: CountryCode,
): string {
  if (!Number.isFinite(amount) || amount < 0) {
    return '';
  }

  const normalizedAmount = Math.floor(amount);
  const currencyPrefix = resolveCurrencyPrefix(countryCode);
  const groupedAmount = formatAmountValue(normalizedAmount, countryCode);

  return `${currencyPrefix}${groupedAmount}`;
}

export function formatAmountValue(
  amount: number,
  countryCode: CountryCode,
): string {
  if (!Number.isFinite(amount) || amount < 0) {
    return '';
  }

  const normalizedAmount = Math.floor(amount);
  const thousandsSeparator = resolveThousandsSeparator(countryCode);

  return normalizedAmount
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
}

export function formatGoalProgressLabelByCountry(
  currentAmount: number,
  targetAmount: number,
  progressPercent: number,
  countryCode: CountryCode,
): string {
  const currencyPrefix = resolveCurrencyPrefix(countryCode);
  const currentAmountLabel = formatAmountValue(currentAmount, countryCode);
  const targetAmountLabel = formatAmountValue(targetAmount, countryCode);

  return `${currencyPrefix}${currentAmountLabel}/${targetAmountLabel} (${progressPercent}%)`;
}

export const parseGoalAmount = parseAmount;
export const formatGoalAmountByCountry = formatCurrency;
export const formatGoalAmountValueByCountry = formatAmountValue;
